import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { requestCartQuote, toQuoteItems, quoteItemsKey } from "../api/deliveryQuote";
import {
  QUOTE_DEBOUNCE_MS,
  estimateFrom,
  estimateUnavailableReason,
  useDropoff,
} from "./useDeliveryEstimate";

/**
 * useCartQuote: every vendor cart of the cart hub, quoted by the server in
 * ONE call (POST /api/quote/cart). Each vendor's cart is its own order,
 * concluded on its own, so each answer is that vendor's alone: its full
 * delivery fee, its goods, its minimum. The shopper's free delivery goes to
 * whichever cart is checked out first, so no vendor's answer carries it; the
 * server says it once for the whole hub (`offerNote`), and the page shows that
 * sentence as it is. Nothing here sums or allocates across vendors.
 *
 * `groups` are { key, vendorId, outletId?, items }. `estimateOf(key)` reads
 * one group's answer in useDeliveryEstimate's words (estimateFrom), so a hub
 * group and a single-vendor cart render the same way. With no area set yet
 * the call is for the goods only, so each group's minimum-order bar is the
 * server's from the first item.
 */
export default function useCartQuote({ groups, enabled = true } = {}) {
  const { hasLocation, latitude, longitude, locationKey } = useDropoff();
  const quoted = (Array.isArray(groups) ? groups : []).filter((group) => group?.key && group?.vendorId);
  const cartKey = quoted
    .map((group) => [group.key, group.vendorId, group.outletId || "", quoteItemsKey(group.items)].join(">"))
    .join("||");

  /* eslint-disable react-hooks/exhaustive-deps */
  const vendors = useMemo(
    () =>
      quoted.map((group) => ({
        key: String(group.key),
        vendorId: group.vendorId,
        outletId: group.outletId || null,
        items: toQuoteItems(group.items),
      })),
    [cartKey]
  );
  const itemsKeys = useMemo(
    () => Object.fromEntries(quoted.map((group) => [String(group.key), quoteItemsKey(group.items)])),
    [cartKey]
  );
  /* eslint-enable react-hooks/exhaustive-deps */
  const wanted =
    enabled && vendors.length > 0 && (hasLocation || vendors.some((vendor) => vendor.items.length > 0));

  const [outcome, setOutcome] = useState(null);
  const latestRequest = useRef(0);

  const fetchCartQuote = useCallback(async () => {
    const requestId = ++latestRequest.current;
    const keys = { itemsKeys, locationKey };
    // The last answer stays under ITS keys while this one is asked.
    setOutcome((previous) => (previous ? { ...previous, status: "loading" } : { status: "loading", answer: null, ...keys }));
    try {
      const answer = await requestCartQuote({
        vendors,
        customerLat: latitude,
        customerLng: longitude,
        goodsOnly: !hasLocation,
      });
      if (requestId !== latestRequest.current) return;
      setOutcome({ status: "ready", answer, ...keys });
    } catch (error) {
      if (requestId !== latestRequest.current) return;
      setOutcome({ status: estimateUnavailableReason(error), answer: null, ...keys });
    }
  }, [vendors, itemsKeys, latitude, longitude, hasLocation, locationKey]);

  useEffect(() => {
    if (!wanted) return undefined;
    const timer = setTimeout(fetchCartQuote, QUOTE_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [wanted, fetchCartQuote]);

  const entries = useMemo(
    () => new Map((outcome?.answer?.vendors || []).map((entry) => [String(entry.key), entry])),
    [outcome]
  );

  const estimateOf = (key) => {
    const id = String(key);
    const entry = entries.get(id);
    const entryOutcome = outcome
      ? {
          status:
            outcome.status !== "ready"
              ? outcome.status
              : !entry
                ? "error"
                : entry.success === false
                  ? estimateUnavailableReason({ response: { data: entry } })
                  : "ready",
          quote: entry && entry.success !== false ? entry : null,
          itemsKey: outcome.itemsKeys?.[id],
          locationKey: outcome.locationKey,
        }
      : null;
    return {
      ...estimateFrom({
        enabled: enabled && id in itemsKeys,
        hasLocation,
        itemsKey: itemsKeys[id],
        locationKey,
        outcome: entryOutcome,
      }),
      refresh: fetchCartQuote,
    };
  };

  return {
    estimateOf,
    // The last answer's line stays while a newer one is asked.
    offerNote: outcome?.answer?.offerNote || null,
    refresh: fetchCartQuote,
  };
}
